import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import type { ReactNode } from "react";

interface CollapsibleFilterHeaderProps {
  collapsed: boolean;
  onToggle: () => void;
  description: ReactNode;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  clearLabel?: string;
  descriptionTestId?: string;
  toggleTestId?: string;
  clearTestId?: string;
  alwaysVisibleContent?: ReactNode;
  children: ReactNode;
}

export function CollapsibleFilterHeader({
  collapsed,
  onToggle,
  description,
  hasActiveFilters = false,
  onClearFilters,
  clearLabel = "Clear",
  descriptionTestId = "text-filter-description",
  toggleTestId = "button-toggle-filter-panel",
  clearTestId = "button-clear-filters",
  alwaysVisibleContent,
  children,
}: CollapsibleFilterHeaderProps) {
  return (
    <>
      <div className={`flex items-center gap-2 min-w-0 ${!collapsed && !alwaysVisibleContent ? 'mb-3 pb-2 border-b' : ''}`}>
        <div className="flex items-center gap-2 shrink-0">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Filter</h3>
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <span className="text-sm text-muted-foreground shrink-0">&mdash;</span>
        <span
          className={`text-sm text-muted-foreground min-w-0 ${collapsed ? 'truncate' : 'break-words'}`}
          data-testid={descriptionTestId}
        >
          {description}
        </span>
        {hasActiveFilters && onClearFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1 shrink-0"
            data-testid={clearTestId}
          >
            <X className="h-3 w-3" />
            {clearLabel}
          </Button>
        )}
        <ExpandCollapseButton
          expanded={!collapsed}
          onClick={onToggle}
          className="ml-auto"
          testId={toggleTestId}
        />
      </div>
      {alwaysVisibleContent && (
        <div className={!collapsed ? 'mb-3 pb-2 border-b' : ''}>
          {alwaysVisibleContent}
        </div>
      )}
      {!collapsed && children}
    </>
  );
}
